import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Extension,
  Tune,
  Gavel,
  SettingsOutlined,
  People,
  Apartment,
} from '@mui/icons-material';

import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';
import ModulesSection from '../components/settings/ModulesSection';
import AtlasConfigSection from '../components/settings/AtlasConfigSection';
import LanguagePreferenceSectionSimple from '../components/LanguagePreferenceSectionSimple';
import TenantUsersSection from '../components/settings/TenantUsersSection';
import TenantOrganisationSection from '../components/settings/TenantOrganisationSection';

interface TabDef {
  key: string;
  label: string;
  icon: React.ReactElement;
  render: () => React.ReactNode;
}

/**
 * Settings page — operational configuration moved out of the Profile page.
 *
 * Tabbed: Modules & Integrations, Preferences, and (non-admin only) ATLAS
 * Configuration. Rendered inside the AppShell chrome like other authed pages.
 */
const SettingsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { tString } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const tabs: TabDef[] = [
    {
      key: 'modules',
      label: tString('settings.tabs.modules'),
      icon: <Extension />,
      render: () => <ModulesSection />,
    },
    {
      key: 'preferences',
      label: tString('settings.tabs.preferences'),
      icon: <Tune />,
      render: () => <LanguagePreferenceSectionSimple />,
    },
    // ATLAS Customs Configuration is hidden for admins (matches Profile gating).
    ...(!user?.is_admin
      ? [{
          key: 'atlas',
          label: tString('settings.tabs.atlas'),
          icon: <Gavel />,
          render: () => <AtlasConfigSection />,
        }]
      : []),
    // Tenant-admin self-service: manage the tenant's users and organisation.
    ...(user?.role === 'tenant_admin'
      ? [
          {
            key: 'tenant-users',
            label: tString('settings.tabs.tenantUsers'),
            icon: <People />,
            render: () => <TenantUsersSection />,
          },
          {
            key: 'tenant-org',
            label: tString('settings.tabs.tenantOrganisation'),
            icon: <Apartment />,
            render: () => <TenantOrganisationSection />,
          },
        ]
      : []),
  ];

  const selected = Math.min(activeTab, tabs.length - 1);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SettingsOutlined color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {tString('settings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tString('settings.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={selected}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1 }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              sx={{ minHeight: 56, textTransform: 'none', fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/*
        All panels stay mounted; inactive ones are hidden. This preserves each
        section's in-progress form state (e.g. unsaved ATLAS edits) across tab
        switches instead of unmounting and reloading server values.
      */}
      {tabs.map((tab, index) => (
        <Box key={tab.key} hidden={selected !== index} role="tabpanel">
          {tab.render()}
        </Box>
      ))}
    </Container>
  );
};

export default SettingsPage;
